import {AfterViewInit, ChangeDetectorRef, Component} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {RouterLink} from '@angular/router';
import {AdministratorApiModel} from '../../../../model/administrator-api.model';
import {AdminService} from '../../../../service/admin.service';
import {AdminEvent} from '../../../../event/admin.event';
import {AdminEventTypeEnum} from '../../../../enum/admin-event-type.enum';
import {AvvvatarsComponent} from '@ngneat/avvvatars';
import {MatDialog} from '@angular/material/dialog';
import {SettingsAdminDialogComponent} from '../../../../component/dialog/admin/settings/settings.component';

/**
 * @class
 * @description This class is responsible for rendering the administrator information component.
 * @export
 * @name InformationAdminPartialLayoutComponent
 */
@Component({
  imports: [
    MatButton,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    RouterLink,
    AvvvatarsComponent
  ],
  selector: 'app-layout-partial-admin-information',
  standalone: true,
  styleUrl: './information.component.scss',
  templateUrl: './information.component.html'
})
export class InformationAdminPartialLayoutComponent implements AfterViewInit {

  /**
   * @description This property contains the default administrator information.
   * @memberof InformationAdminPartialLayoutComponent
   * @name InformationAdminPartialLayoutComponent#administrator
   * @property
   * @protected
   * @type {AdministratorApiModel?}
   */
  public administrator?: AdministratorApiModel;

  /**
   * @constructor
   * @description This method instantiates a new instance of our administrator information component.
   * @memberof InformationAdminPartialLayoutComponent
   * @name InformationAdminPartialLayoutComponent#constructor
   * @public
   */
  public constructor(
    /**
     * @description This property contains the administrator service provider.
     * @memberof InformationAdminPartialLayoutComponent
     * @name InformationAdminPartialLayoutComponent#adminService
     * @param {AdminService} adminService The injected instance of our admin service provider.
     * @param {ChangeDetectorRef} changeDetector The change detector reference.
     * @private
     * @property
     * @readonly
     * @type {AdminService}
     */
    private readonly adminService: AdminService,
    /**
     * @description This property contains the change detector reference.
     * @memberof InformationAdminPartialLayoutComponent
     * @name InformationAdminPartialLayoutComponent#changeDetector
     * @private
     * @property
     * @readonly
     * @type {ChangeDetectorRef}
     */
    private readonly changeDetector: ChangeDetectorRef,
    /**
     * @description This property contains the dialog service provider.
     * @memberof InformationAdminPartialLayoutComponent
     * @name InformationAdminPartialLayoutComponent#dialog
     * @private
     * @property
     * @readonly
     * @type {MatDialog}
     */
    private readonly dialog: MatDialog
  ) {
  }

  /**
   * @description This method loads the administrator details.
   * @memberof InformationAdminPartialLayoutComponent
   * @name InformationAdminPartialLayoutComponent#loadAdministratorDetails
   * @method
   * @private
   * @returns {void}
   */
  private loadAdministratorDetails(): void {

    // Load the administrator details from the .NET backend API.
    this.adminService.details().subscribe((a: AdministratorApiModel): void => {

      // Reset the administrator object into the component instance.
      this.administrator = a;

      // Have our change detector detect any changes.
      this.changeDetector.detectChanges();
    });
  }

  /**
   * @description This method opens the settings dialog.
   * @memberof InformationAdminPartialLayoutComponent
   * @name InformationAdminPartialLayoutComponent#openSettingsDialog()
   * @method
   * @protected
   * @returns {void}
   */
  protected openSettingsDialog(): void {
    this.dialog.open(SettingsAdminDialogComponent, {

      // Pass the data to the dialog.
      data: {

        // Pass the administrator object to the dialog.
        administrator: this.administrator,

        // We'll want to disable toggles.
        disableToggles: true,

        // This is for the current user.
        isMe: true,

        // Password is not required.
        passwordRequired: false
      },

      // Disable the dialog's ability to close by clicking outside of it.
      disableClose: true,

      // Set the dialog's width to 1024 pixels.
      width: '1024px'
    });
  }

  /**
   * @abstract
   * @description This method is called after Angular has fully initialized the component's view.
   * @memberof InformationAdminPartialLayoutComponent
   * @name InformationAdminPartialLayoutComponent#ngAfterViewInit
   * @method
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Subscribe to the appropriate events of the administrator service if there isn't an administrator session.
    if (!this.adminService.online()) this.adminService.events.subscribe((e: AdminEvent): void => {

      // Check for a session-started or session-continued event then load the administrator details.
      if (e.type === AdminEventTypeEnum.SessionStart || e.type === AdminEventTypeEnum.SessionContinued) this.loadAdministratorDetails();
    });

    // Otherwise, load the administrator details.
    else this.loadAdministratorDetails();
  }
}
